import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import PageContent from "../components/pageContent";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { FormatString } from "../helpers/stringHelpers";
import { checkCrud } from "../helpers/userRolesHelper";
import ProductsPopup from "../popups/ProductsPopup";
import { TranslationProvider } from "../providers/TranslationContext";
import { addNotification } from "../redux/actions";

import { apiDeleteProduct, apiGetAllProducts, apiGetProductGroups } from "../services/ProductsService";

interface Props {
  contextLevel: string;
}

const entityName = 'product';
const translationPrefix = 'Products';
const translationModel = { prefix: translationPrefix };

const ProductsPage = (props: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: translationPrefix });

  const { t: tDiscountsFields } = useTranslation(undefined, { keyPrefix: 'Discounts.Fields' });

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [] });
  const [productGroups, setProductGroups] = useState([]);
  const [productPopupData, setProductPopupData] = useState({
    visible: false,
    id: undefined,
  });

  const dispatch = useDispatch();

  const [canCreate, setCanCreate] = useState(false);
  const [canRead, setCanRead] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const userInfo = useSelector((state: any) => state.userInfo);

  const setRoles = () => {
    setCanCreate(checkCrud(entityName, 'create', userInfo));
    setCanRead(checkCrud(entityName, 'read', userInfo));
    setCanUpdate(checkCrud(entityName, 'update', userInfo));
    setCanDelete(checkCrud(entityName, 'delete', userInfo));
  }

  useEffect(() => {
    setRoles();
  }, [userInfo.UserRoles])

  useEffect(() => {
    loadData();
    apiGetProductGroups(false)
      .then((resp: any) => {
        setProductGroups(resp.data);
      })
      .catch((ex: any) => {

      });
  }, []);

  const loadData = () => {
    apiGetAllProducts(false).then((responseData) => {

      let rows = responseData.data;

      rows.forEach((row) => {
        if (row.discountBaseGroupName && 
          row.discountBaseGroupName.length > 0 && 
          row.discountBaseGroupName != '-') {
          row.discountBaseGroupName = tDiscountsFields(row.discountBaseGroupName);
        }
      })

      setData({
        rows: rows,
      });

      setLoading(false);
    });
  };

  const handleDeleteRow = (row) => {
    apiDeleteProduct(row.id)
      .then((responseData) => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteSuccess'), row.name), type: 'success' }));
        loadData();

        setLoading(false);
      })
      .catch((err: any) => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteFailure'), row.name), type: 'error' }));
      });
  }

  return (
    <TranslationProvider value={translationModel}>
      {productPopupData.visible && (
        <ProductsPopup
          id={productPopupData.id}
          products={data.rows}
          productGroups={productGroups}
          refreshData={() => loadData()}
          handleCancel={() => {
            setProductPopupData({ visible: false, id: undefined });
          }}
        />
      )}
      <TopBarWithBreadCrumb
        contextLevel={props.contextLevel}
        pageLevel={t('Label')}
        rightAreaComponent={
          <AddButtonInTopBar
            setPopupData={setProductPopupData}
            text={t('CreateText')}
            enabled={canCreate}
          />
        }
      />

      <PageContent>
        <DataGrid
          loading={loading}
          rows={data.rows}
          showDeleteButton={canDelete}
          showEditButton={canUpdate}
          handleEditAction={(id: any) => {
            setProductPopupData({ visible: true, id: id });
          }}
          onDeleteRow={handleDeleteRow}
          cols={[
            { name: t('Fields.sortOrder'), dataField: "sortOrder" },
            { name: t('Fields.name'), dataField: "name", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.preferLitres'), dataField: "preferLitres", fieldType: 'boolean', sortable: true },
            { name: t('Fields.noteInternal'), dataField: "noteInternal" },
            { name: t('Fields.discountBaseGroupName'), dataField: "discountBaseGroupName", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.path'), dataField: "path", sortable: true },
          ]}
        />
      </PageContent>
    </TranslationProvider>
  );
};

export default ProductsPage;
